// Meetings ledger — calendar meetings the assistant follows up on, recordings
// turned into notes, and video calls the notes bot sat in.

import { useEffect, useMemo } from "react";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { recordingFileUrl } from "@/api/meetingRecordings";
import { mergePages, nextCursor, olderCursor, PAGE, type FileProbe } from "@/features/meetings/detailModel";
import { apiFetch, errorText } from "@/lib/api";
import type { components } from "@/types/api.gen";

/**
 * What a newer server says on a meeting, typed here until api.gen.ts is
 * generated again with it. Optional, so an older server (which does not
 * send it) reads the same: the page then asks the file route.
 */
interface MeetingLater {
  /** false: the recording is not where the page can play it (a voice note left in the agent's container). */
  recording_playable?: boolean | null;
}
export type Meeting = components["schemas"]["Meeting"] & MeetingLater;
export type MeetingTranscript = components["schemas"]["MeetingTranscript"];
export type MeetingAction = components["schemas"]["MeetingAction"];
export type MeetingList = Omit<components["schemas"]["MeetingList"], "meetings"> & { meetings: Meeting[] };
export type MeetingPatchBody = components["schemas"]["MeetingPatchBody"];
export type NotesBotBody = components["schemas"]["NotesBotBody"];
export type NotesBotLive = components["schemas"]["NotesBotLive"];
export type NotesBotSettings = components["schemas"]["NotesBotSettings"];
export type NotesBotAgent = components["schemas"]["NotesBotAgent"];
export type NotesBotAgentBody = components["schemas"]["NotesBotAgentBody"];
export type NotesBotSeriesBody = components["schemas"]["NotesBotSeriesBody"];

const key = (customerId: string | null) => ["meetings", customerId];
const base = (c: string) => `/api/customers/${c}`;

export function useMeetings(customerId: string | null, poll?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: key(customerId),
    enabled: customerId !== null,
    queryFn: ({ signal }) => apiFetch<MeetingList>(`${base(customerId!)}/meetings?limit=${PAGE}`, { signal }),
    refetchInterval: poll?.refetchInterval ?? 30_000,
  });
}

/**
 * The meetings older than the newest page (useMeetings), a page at a time,
 * only once "Show older" is pressed: `wanted` pages are kept loaded. Each
 * page is the PAGE rows the server sorts before the last one loaded
 * (detailModel.nextCursor). A newest page that moves on (new rows) starts
 * the older pages again from its own last row, and as many are read again,
 * so nothing ever falls between the two. Under the meetings key: every
 * meetings change reads them again.
 */
export function useOlderMeetings(customerId: string | null, main: readonly Meeting[] | undefined, wanted: number) {
  const cursor = olderCursor(main);
  const q = useInfiniteQuery({
    queryKey: ["meetings", customerId, "older", cursor],
    enabled: customerId !== null && cursor !== null && wanted > 0,
    initialPageParam: cursor ?? "",
    queryFn: ({ pageParam, signal }) =>
      apiFetch<MeetingList>(`${base(customerId!)}/meetings?to=${encodeURIComponent(pageParam)}&limit=${PAGE}`, { signal }),
    getNextPageParam: (last, pages, param) => {
      const seen = new Set((main ?? []).map((m) => m.id));
      for (const p of pages.slice(0, -1)) for (const m of p.meetings) seen.add(m.id);
      return nextCursor(last.meetings, seen, param);
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
  });
  const { data, hasNextPage, isFetchingNextPage, isError, isPlaceholderData, fetchNextPage } = q;
  const loaded = data?.pages.length ?? 0;
  useEffect(() => {
    if (data && !isPlaceholderData && loaded < wanted && hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [data, isPlaceholderData, loaded, wanted, hasNextPage, isFetchingNextPage, isError, fetchNextPage]);
  const rows = useMemo(() => mergePages(main ?? [], (data?.pages ?? []).map((p) => p.meetings)), [main, data]);
  return {
    /** The newest page, then every older row loaded. */
    rows,
    /** More may be older than what is loaded (a full newest page and no short older page yet). */
    more: cursor !== null && (loaded === 0 || !!hasNextPage),
    loading: q.isFetching && (loaded < wanted || isFetchingNextPage),
    error: q.error,
    /** After a failed page: ask for it again (the first one, or the next). */
    retry: () => void (loaded === 0 ? q.refetch() : fetchNextPage()),
  };
}

/** One meeting read on its own: one the loaded pages do not hold (an old link). */
export function useMeeting(customerId: string | null, meetingId: string | null, enabled = true) {
  return useQuery({
    queryKey: ["meetings", customerId, "one", meetingId],
    enabled: enabled && customerId !== null && meetingId !== null,
    queryFn: ({ signal }) => apiFetch<Meeting>(`${base(customerId!)}/meetings/${encodeURIComponent(meetingId!)}`, { signal }),
    staleTime: 60_000,
  });
}

/** A meeting's transcript text, read only when its Transcript fold is opened (the list never carries it). */
export function useMeetingTranscript(customerId: string, meetingId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["meetings", customerId, "transcript", meetingId],
    enabled,
    queryFn: ({ signal }) =>
      apiFetch<MeetingTranscript>(`${base(customerId)}/meetings/${encodeURIComponent(meetingId)}/transcript`, { signal }),
    staleTime: 60_000,
  });
}

/**
 * One agent's meetings that start before `before` (an ISO time), newest
 * first: what "Move to a calendar meeting…" picks from. The window's edge
 * is a day past what it shows (the server compares times as text, so an
 * offset never cuts one off); detailModel.moveCandidates narrows it.
 */
export function useAgentMeetingsBefore(customerId: string | null, agent: string, before: string | null, enabled = true) {
  return useQuery({
    queryKey: ["meetings", customerId, "before", agent, before],
    enabled: enabled && customerId !== null && before !== null,
    queryFn: ({ signal }) =>
      apiFetch<MeetingList>(
        `${base(customerId!)}/meetings?agent=${encodeURIComponent(agent)}&to=${encodeURIComponent(before!)}&limit=200`,
        { signal },
      ),
    staleTime: 60_000,
  });
}

/**
 * Whether the file route has a meeting's audio, for a server that does not
 * say so on the meeting: its first byte is asked for and the rest never
 * read. 404 = not there; anything else lets the player try.
 */
export function useRecordingProbe(customerId: string, meetingId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["meetings", customerId, "recording-probe", meetingId],
    enabled,
    queryFn: async ({ signal }): Promise<FileProbe> => {
      const res = await fetch(recordingFileUrl(customerId, meetingId), { headers: { Range: "bytes=0-0" }, credentials: "include", signal });
      void res.body?.cancel().catch(() => undefined);
      return res.status === 404 ? "missing" : "found";
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/**
 * Move an entry the agent filed itself (`from`) onto the calendar meeting
 * `into`: its notes, actions, recording and transcript go there and the
 * entry is removed (the server's own rules decide; a refusal is shown in
 * words by the dialog).
 */
export function useMoveMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, into, from }: { customerId: string; into: string; from: string }) =>
      apiFetch<Meeting>(`${base(customerId)}/meetings/${encodeURIComponent(into)}/absorb`, { method: "POST", body: { from } }),
    // Not awaited: the caller opens the calendar meeting first, so the panel never closes while the moved entry drops out.
    onSettled: (_d, _e, v) => {
      void qc.invalidateQueries({ queryKey: key(v.customerId) });
    },
    meta: { errorHandled: true },
  });
}

/** errorHandled: the screen shows this mutation's failure itself, so the
 * app-wide handler does not toast it as well. Set per use where a hook is
 * shared by a screen that shows the error and one that does not. */
export interface MutationUse {
  errorHandled?: boolean;
}

function invalidating<TArgs extends { customerId: string }, TOut>(
  fn: (args: TArgs) => Promise<TOut>,
  defaults: MutationUse = {},
) {
  return (use: MutationUse = defaults) => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: fn,
      onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: key(v.customerId) }),
      meta: use.errorHandled ? { errorHandled: true } : undefined,
    });
  };
}

// Follow-up settings, the open meeting's Restore and "Dismiss all" show
// their own errors; a tick (usePatchMeeting) is also sent from screens that
// do not, so it says so per use.
const SHOWN: MutationUse = { errorHandled: true };

export const useSetMeetingsAgent = invalidating(
  ({ customerId, template, enabled, followups }: { customerId: string; template: string; enabled: boolean; followups?: "off" | "daily" | "each" }) =>
    apiFetch<MeetingList>(`${base(customerId)}/agents/${encodeURIComponent(template)}/meetings`, {
      method: "PUT",
      body: followups ? { enabled, followups } : { enabled }, }),
  SHOWN,
);

export const useSyncMeetings = invalidating(
  ({ customerId, template }: { customerId: string; template: string }) =>
    apiFetch<MeetingList>(`${base(customerId)}/agents/${encodeURIComponent(template)}/meetings/sync`, {
      method: "POST",
    }),
  SHOWN,
);

export const usePatchMeeting = invalidating(
  ({ customerId, id, body }: { customerId: string; id: string; body: MeetingPatchBody }) =>
    apiFetch<Meeting>(`${base(customerId)}/meetings/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: body, }),
);

// ─── the notes bot, now (the Record screen's "Send <agent> to the call") ───

export const useSendNotesBot = invalidating(
  ({ customerId, body }: { customerId: string; body: NotesBotBody }) =>
    apiFetch<Meeting>(`${base(customerId)}/meetings/bot`, { method: "POST", body }),
  SHOWN,
);

export const useStopNotesBot = invalidating(
  ({ customerId, id }: { customerId: string; id: string }) =>
    apiFetch<Meeting>(`${base(customerId)}/meetings/${encodeURIComponent(id)}/bot`, { method: "DELETE" }),
  SHOWN,
);

/** How far the bot in `meetingId` got; polled every 4 s while it is on its way or in the call, every 20 s after. */
export function useNotesBot(customerId: string | null, meetingId: string | null) {
  return useQuery({
    queryKey: ["meetings", customerId, "bot", meetingId],
    enabled: customerId !== null && meetingId !== null,
    queryFn: ({ signal }) =>
      apiFetch<NotesBotLive>(`${base(customerId!)}/meetings/${encodeURIComponent(meetingId!)}/bot`, { signal }),
    refetchInterval: (q) => {
      const bot = q.state.data?.meeting.bot;
      return bot === "requested" || bot === "joined" ? 4_000 : bot === "done" && !q.state.data?.meeting.notes_md ? 20_000 : false;
    },
  });
}

// ─── the notes bot joining the calls an agent is invited to ───

/** Per agent with meetings on: whether its notes bot joins the calls it is invited to, and the address that counts. Kept under the meetings key, so every meetings change reads it again. */
export function useNotesBotSettings(customerId: string | null) {
  return useQuery({
    queryKey: ["meetings", customerId, "notes-bot"],
    enabled: customerId !== null,
    queryFn: ({ signal }) => apiFetch<NotesBotSettings>(`${base(customerId!)}/notes-bot`, { signal }),
  });
}

/** Switch it per agent (the settings and the meetings list are read again: switching off calls the invite's requests off). */
export const useSetNotesBotAgent = invalidating(
  ({ customerId, template, body }: { customerId: string; template: string; body: NotesBotAgentBody }) =>
    apiFetch<NotesBotAgent>(`${base(customerId)}/agents/${encodeURIComponent(template)}/notes-bot`, { method: "PUT", body }),
  SHOWN,
);

/** Not to any meeting of this one's series (`send: false`), or to every one again (`true`). */
export const useSetNotesBotSeries = invalidating(
  ({ customerId, id, body }: { customerId: string; id: string; body: NotesBotSeriesBody }) =>
    apiFetch<Meeting>(`${base(customerId)}/meetings/${encodeURIComponent(id)}/bot-series`, { method: "PUT", body }),
  SHOWN,
);

// ─── bulk dismiss / restore ───
//
// There is no bulk endpoint: "Dismiss all" is the same single PUT
// {status: "skipped"} that "Nothing to follow up" sends, looped with a
// small concurrency cap. patch() on the server is a plain UPDATE (no agent
// turn, idempotent, never undone by the calendar sync), so a partial run
// is safe to retry. The list is invalidated ONCE at the end (looping
// usePatchMeeting would refetch all 300 rows after every call).

const DISMISS_CONCURRENCY = 4;

export interface DismissResult {
  done: string[];
  failed: { id: string; error: string }[];
}

export function useDismissMeetings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerId,
      ids,
      onProgress,
    }: {
      customerId: string;
      ids: string[];
      onProgress?: (settled: number, total: number) => void;
    }): Promise<DismissResult> => {
      const out: DismissResult = { done: [], failed: [] };
      let next = 0;
      let settled = 0;
      const worker = async () => {
        while (next < ids.length) {
          const id = ids[next++];
          try {
            await apiFetch<Meeting>(`${base(customerId)}/meetings/${encodeURIComponent(id)}`, {
              method: "PUT",
              body: { status: "skipped" } satisfies MeetingPatchBody,
            });
            out.done.push(id);
          } catch (e) {
            out.failed.push({ id, error: errorText(e) });
          }
          settled += 1;
          onProgress?.(settled, ids.length);
        }
      };
      await Promise.all(Array.from({ length: Math.min(DISMISS_CONCURRENCY, ids.length) }, worker));
      return out;
    },
    onSettled: (_d, _e, v) => qc.invalidateQueries({ queryKey: key(v.customerId) }),
    meta: { errorHandled: true },
  });
}

/**
 * Undo a dismissal. Back to "prompted" when the agent had already asked
 * (so it is not asked again), "upcoming" for a meeting still ahead, else
 * "ended" (the agent will ask about it at its next follow-up round).
 */
export const useRestoreMeeting = invalidating(
  ({ customerId, meeting }: { customerId: string; meeting: Pick<Meeting, "id" | "prompted_at" | "start_at"> }) => {
    const ahead = meeting.start_at ? Date.parse(meeting.start_at) > Date.now() : false;
    const status: MeetingPatchBody["status"] = meeting.prompted_at ? "prompted" : ahead ? "upcoming" : "ended";
    return apiFetch<Meeting>(`${base(customerId)}/meetings/${encodeURIComponent(meeting.id)}`, {
      method: "PUT",
      body: { status },
    });
  },
  SHOWN,
);
